import React, { useRef, useState, useEffect } from "react";
import MapTo from "@utils/mapto";
import { AllContentContainer } from "./Hotspot.css";
import Image from "@components/Image";

/**
 * Hotspot Component - Renders an image with interactive hotspot points
 * 
 * @param {string} src - Image source URL
 * @param {string} alt - Image alternative text
 * @param {Array} hotspots - Array of hotspot coordinates with {name, x, y} format
 * @param {Object} hotspotContent - Content to display for each hotspot
 * @param {Object} style - Custom styles for the image
 * @param {string} className - CSS class for the image
 * @param {string} pictureClassName - CSS class for the picture element
 * @param {string} srcMobile - Alternative image source for mobile devices
 */
const Hotspot = ({ 
    src, 
    alt, 
    hotspots = [], 
    hotspotContent, 
    style, 
    className, 
    pictureClassName, 
    srcMobile 
}) => {
    const imageRef = useRef(null);
    const [imageSize, setImageSize] = useState({ width: 0, height: 0 });
    const [activeHotspot, setActiveHotspot] = useState(null);
    const [showAllContent, setShowAllContent] = useState(false);
    const [isMobile, setIsMobile] = useState(false);
    
    useEffect(() => {
        const updateImageSize = () => {
            if (imageRef.current) {
                setImageSize({
                    width: imageRef.current.offsetWidth,
                    height: imageRef.current.offsetHeight
                });
            }
            
            // Check if mobile based on CSS display properties
            // This uses the same logic that controls the visibility of desktop/mobile images
            const isDesktopVisible = window.getComputedStyle(document.querySelector('.hidden.lg\\:block')).display !== 'none';
            setIsMobile(!isDesktopVisible);
        };
        
        updateImageSize();
        window.addEventListener('resize', updateImageSize);
        return () => {
            window.removeEventListener('resize', updateImageSize);
        };
    }, []);

    // const ImageSrc = srcMobile ? srcMobile : src;

    const renderContent = (items, className = "") => {
        if (!items) return null;
        
        const id = items.contentfulid || items.id;
        
        // Handle Contentful container or any content type with MapTo
        return <MapTo items={items} key={id} id={id} className={className} />;
    };

    const findMatchingContent = (hotspot) => {
        if (!hotspot || !hotspotContent?.items?.length) return null;
        
        const hotspotName = hotspot.name || '';
        
        // First try to find a match by ID/name
        const matchedItem = hotspotContent.items.find(item => {
            if (!item?.contentfulid) return false;
            
            const contentId = item.contentfulid;
            
            // Check for substring match (case insensitive)
            if (hotspotName.toLowerCase().includes(contentId.toLowerCase()) || 
                contentId.toLowerCase().includes(hotspotName.toLowerCase())) {
                return true;
            }
            
            // Check for numeric match (e.g., "Step 1" matches with "step1")
            const hotspotNumber = hotspotName.match(/\d+/);
            const contentNumber = contentId.match(/\d+/);
            
            return hotspotNumber && contentNumber && 
                   hotspotNumber[0] === contentNumber[0];
        });
        
        if (matchedItem) return matchedItem;
        
        // Fallback to index-based matching
        const index = hotspots.findIndex(h => h === hotspot);
        return (index >= 0 && index < hotspotContent.items.length) 
            ? hotspotContent.items[index] 
            : null;
    };

    const getHotspotContent = (index) => {
        if (!hotspots?.[index]) return null;
        return findMatchingContent(hotspots[index]);
    };

    const getHotspotMarkerStyle = (hotspot) => {
        if (!hotspot) return {};
        
        return {
            position: 'absolute',
            left: `${hotspot.x - 2}%`,
            top: `${hotspot.y + 8}%`,
            transform: 'translate(-50%, -50%)',
            width: `${hotspot.width}%`,
            height: `${hotspot.height}%`,
            zIndex: 5,
            cursor: 'pointer',
        };
    };

    const getPopupStyle = (hotspot) => {
        if (!hotspot) return {};
        
        return {
            position: 'absolute',
            left: `calc(${hotspot.x - 2}% + ${hotspot.width / 2}% + 16px)`,
            top: `${hotspot.y + 8}%`,
            transform: 'translateY(-50%)',
            zIndex: 10,
            backgroundColor: 'rgba(255, 255, 255, 0.75)',
            borderRadius: '48px',
            maxWidth: '330px',
            width: '100%',
            maxHeight: '100%',
            backdropFilter: 'blur(50px)',
        };
    };

    const toggleAllContent = () => setShowAllContent(!showAllContent);
    const closeAllContent = () => setShowAllContent(false);
    
    // Mouse event handlers for hotspots
    const handleHotspotMouseEnter = (index) => setActiveHotspot(index);
    const handleHotspotMouseLeave = () => setActiveHotspot(null);
    
    const renderPopupContent = (hotspot, index) => {
        const contentItem = getHotspotContent(index);
        if (!contentItem) return null;
        
        const contentfulId = contentItem?.contentfulid;
        
        // Check if hotspot.name matches contentfulId (case insensitive)
        const nameMatches = hotspot.name && contentfulId && 
            (hotspot.name.toLowerCase().includes(contentfulId.toLowerCase()) || 
             contentfulId.toLowerCase().includes(hotspot.name.toLowerCase()));
        
        // Only render if there's a match or if contentfulId doesn't exist
        if (!contentfulId || nameMatches) {
            return (
                <>
                    <div className="hotspot-popup-content">
                        {renderContent(contentItem, "mx-0 container-custom-style")}
                    </div>
                </>
            );
        }
        
        return null;
    };

    return (
        <div className="hotspot-container relative">
            <div className="hotspot-image-wrapper">
                <p className="lg:block hidden font-p font-bold text-center mb-12 text-black/70">
                    Hover over the numbers to learn more
                </p>
                
                <div className="relative">
                    <Image
                        pictureClassName={`${pictureClassName} hotspot-image mx-auto`}
                        src={src}
                        alt={alt}
                        srcMobile={srcMobile}
                        style={{...style, maxWidth: '100%', height: 'auto'}}
                        className={`${className} mx-auto`}
                        imageRef={imageRef}
                    />
                    
                    {hotspots && imageRef.current && (
                        <div 
                            className="hotspot-markers lg:block hidden left-0 lg:left-[26.5%] top-0 absolute h-full" 
                            style={{ width: `${imageSize.width}px` }}
                        >
                            {hotspots.map((hotspot, index) => (
                                <React.Fragment key={index}>
                                    <div 
                                        style={getHotspotMarkerStyle(hotspot)}
                                        className={`hotspot-marker ${activeHotspot === index ? 'active' : ''}`}
                                        onMouseEnter={() => handleHotspotMouseEnter(index)}
                                        onMouseLeave={handleHotspotMouseLeave}
                                        aria-label={`Hotspot ${index}${hotspot.name ? `: ${hotspot.name}` : ''}`}
                                    >
                                    </div>
                                    

                                        <div 
                                        className={`${'hotspot' + index} hotspot-popup p-10 container-gradient-light ${activeHotspot === index ? 'block' : 'hidden'}`}
                                            style={getPopupStyle(hotspot)}
                                            onMouseEnter={() => handleHotspotMouseEnter(index)}
                                        >
                                            {renderPopupContent(hotspot, index)}
                                        </div>

                                </React.Fragment>
                            ))}
                        </div>
                    )}
                </div>
                
                <p 
                    className="font-p font-bold text-center mt-12 underline text-dark_green cursor-pointer lg:block hidden" 
                    id="view-all"
                    onClick={toggleAllContent}
                >
                    View all
                </p>
            </div>
            
            {/* All Content Section */}
            {hotspotContent?.items && (
                <AllContentContainer 
                    className={`${showAllContent ? 'lg:absolute' : 'lg:hidden'} inset-0 flex items-center justify-center z-50`}
                >
                    <div className="lg:mx-auto mx-0 rounded-all_48 lg:max-w-[886px] w-full py-8 px-6 lg:py-[96px] lg:px-[96px] container-gradient">
                        <div className="absolute top-8 right-8 justify-end items-center mb-6 lg:flex hidden">
                            <button 
                                className="flex items-center justify-center text-green font-thin !w-[56px] h-[56px] px-5 text-[40px] border-gradient-button"
                                onClick={closeAllContent}
                            >
                                ×
                            </button>
                        </div>
                        <div>
                            {hotspotContent.items.map((item, index) => (
                                <div key={index} className={`flex flex-row items-center lg:gap-6 gap-4 hotspot-content-item last:pb-0 pb-6`}>
                                    <span className="py-1 px-4 lg:p-2.5 lg:w-[80px] lg:h-[80px] w-[50px] h-[50px] bg-orange rounded-full text-[30px] lg:text-h2 font-bold text-dark_green text-center mt-auto mb-auto lg:flex lg:items-center lg:justify-center block">
                                        {index + 1}
                                    </span>
                                    {renderContent(item)}
                                </div>
                            ))}
                        </div>
                    </div>
                </AllContentContainer>
            )}
            
            {/* Hidden section for backwards compatibility */}
            <div className="hidden hotspot-all-content mb-40 bg-light_green py-20">
                {hotspotContent?.items?.map((item, index) => (
                    <div key={index} className="hotspot-content-item">
                        {renderContent(item)}
                    </div>
                ))}
            </div>
        </div>
    );
};

export default Hotspot;
